import $ from 'jquery';

/**
 * Usage tracking and upselling system for free-tier users
 */

const usageDebugEnabled = Boolean(
  window.__USAGE_DEBUG__ || localStorage.getItem('enableUsageDebug') === 'true'
);
const usageLog = (...args) => {
  if (usageDebugEnabled) {
    console.log('[usage]', ...args);
  }
};

window.UsageTracker = {
  // Track the current usage state
  usageData: null,

  // Initialize usage tracking
  init: function () {
    usageLog('Initializing usage tracking system');

    // Listen for user-ready events to get usage data
    $(document).on('user-ready', this.onUserReady.bind(this));

    // Listen for query responses to update usage data
    this.setupQueryResponseListeners();
  },

  // Handle user ready event
  onUserReady: function (event, userData) {
    if (userData && userData.usage) {
      this.usageData = userData.usage;
      usageLog('Updated usage data', this.usageData);

      // Update UI elements
      this.updateUsageUI();

      // Check if we should show upsell
      if (this.shouldShowUpsell()) {
        setTimeout(() => {
          this.showUpsellDialog();
        }, 1000); // Delay to let page load
      }
    }
  },

  // Setup listeners for query responses
  setupQueryResponseListeners: function () {
    // Hook into existing query functions to capture responses
    if (window.dataAccessor && window.dataAccessor.makeQuery) {
      const originalMakeQuery = window.dataAccessor.makeQuery;
      window.dataAccessor.makeQuery = function (query, callback) {
        return originalMakeQuery.call(this, query, function (result) {
          // Update usage data from response
          if (result && result.metadata && result.metadata.usage) {
            window.UsageTracker.updateUsageFromResponse(result.metadata.usage);
          }

          // Call original callback
          if (callback) callback(result);
        });
      };
    }
  },

  // Update usage data from query response
  updateUsageFromResponse: function (usageData) {
    this.usageData = usageData;
    usageLog('Updated usage from query response', usageData);

    // Update UI
    this.updateUsageUI();

    // Check if we should show upsell dialog
    if (this.shouldShowUpsell()) {
      this.showUpsellDialog();
    }

    // Check if trial expired
    if (usageData.trial_expired) {
      this.handleTrialExpired();
    }
  },

  // Check if upsell should be shown
  shouldShowUpsell: function () {
    if (!this.usageData) return false;
    if (this.usageData.is_subscribed) return false;

    // Show after 3rd query, but not if already expired (handled separately)
    return this.usageData.should_show_upsell && !this.usageData.trial_expired;
  },

  // Show the upsell dialog
  showUpsellDialog: function () {
    // Don't show again if recently shown (prevent spam)
    if (this.recentlyShownUpsell) return;

    this.recentlyShownUpsell = true;
    setTimeout(() => {
      this.recentlyShownUpsell = false;
    }, 30000); // Reset after 30 seconds

    const remainingQueries = this.usageData.remaining_trial_queries;
    const usedQueries = this.usageData.trial_queries_used;

    // Create upsell dialog HTML
    const dialogHTML = `
            <div id="usage-upsell-overlay" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 10000; display: flex; justify-content: center; align-items: center;">
                <div style="background: white; padding: 40px; border-radius: 12px; max-width: 600px; width: 90%; box-shadow: 0 10px 30px rgba(0,0,0,0.3); text-align: center; position: relative;">
                    <button id="close-upsell" style="position: absolute; top: 15px; right: 20px; background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
                    
                    <div style="margin-bottom: 20px;">
                        <h2 style="color: #333; margin-bottom: 10px;">🚀 You're doing great!</h2>
                        <p style="color: #666; font-size: 16px; margin-bottom: 20px;">You've used <strong>${usedQueries} of your 5 free queries</strong>. Only ${remainingQueries} left!</p>
                    </div>
                    
                    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 8px; margin-bottom: 20px;">
                        <h3 style="margin-bottom: 15px;">🎯 Upgrade to Pro</h3>
                        <div style="font-size: 36px; font-weight: bold; margin-bottom: 5px;">$19<span style="font-size: 18px; opacity: 0.8;">/month</span></div>
                        <div style="opacity: 0.9; margin-bottom: 20px;">Get unlimited queries + premium features</div>
                        
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; text-align: left;">
                            <div>✅ Unlimited AI queries</div>
                            <div>✅ Priority processing</div>
                            <div>✅ Advanced chart types</div>
                            <div>✅ Data export options</div>
                            <div>✅ Team collaboration</div>
                            <div>✅ Premium support</div>
                        </div>
                    </div>
                    
                    <div style="display: flex; gap: 15px; justify-content: center; margin-top: 25px;">
                        <button id="upgrade-now" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 30px; border: none; border-radius: 6px; font-weight: bold; cursor: pointer; font-size: 16px;">
                            Upgrade Now
                        </button>
                        <button id="continue-trial" style="background: #f5f5f5; color: #666; padding: 15px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 16px;">
                            Continue Trial (${remainingQueries} left)
                        </button>
                    </div>
                </div>
            </div>
        `;

    // Add dialog to page
    $('body').append(dialogHTML);

    // Setup event listeners
    $('#close-upsell, #continue-trial').click(() => {
      $('#usage-upsell-overlay').fadeOut(300, function () {
        $(this).remove();
      });
    });

    $('#upgrade-now').click(() => {
      this.openSubscriptionDialog();
      $('#usage-upsell-overlay').remove();
    });

    // Mark upsell as shown
    this.markUpsellShown();

    usageLog('Upsell dialog shown');
  },

  // Handle trial expired
  handleTrialExpired: function () {
    const dialogHTML = `
            <div id="trial-expired-overlay" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); z-index: 10001; display: flex; justify-content: center; align-items: center;">
                <div style="background: white; padding: 40px; border-radius: 12px; max-width: 500px; width: 90%; box-shadow: 0 15px 40px rgba(0,0,0,0.4); text-align: center;">
                    
                    <div style="margin-bottom: 25px;">
                        <div style="font-size: 48px; margin-bottom: 15px;">⏰</div>
                        <h2 style="color: #e74c3c; margin-bottom: 10px;">Free Queries Used</h2>
                        <p style="color: #666; font-size: 16px;">You've used all 5 free queries. Upgrade to a paid plan to keep creating visualizations.</p>
                    </div>
                    
                    <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 25px; border-radius: 8px; margin-bottom: 25px;">
                        <h3 style="margin-bottom: 10px;">Unlock Unlimited Access</h3>
                        <div style="font-size: 32px; font-weight: bold; margin-bottom: 15px;">$19<span style="font-size: 16px; opacity: 0.8;">/month</span></div>
                        <div style="opacity: 0.9;">Join thousands of users creating data visualizations</div>
                    </div>
                    
                    <button id="upgrade-required" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 40px; border: none; border-radius: 6px; font-weight: bold; cursor: pointer; font-size: 18px; width: 100%;">
                        Upgrade to Continue
                    </button>
                </div>
            </div>
        `;

    $('body').append(dialogHTML);

    $('#upgrade-required').click(() => {
      this.openSubscriptionDialog();
      $('#trial-expired-overlay').remove();
    });

    usageLog('Trial expired dialog shown');
  },

  // Update usage UI elements
  updateUsageUI: function () {
    if (!this.usageData) return;

    // Create or update usage indicator
    let indicator = $('#usage-indicator');
    if (indicator.length === 0) {
      // Create usage indicator
      const indicatorHTML = `
                <div id="usage-indicator" style="position: fixed; top: 20px; right: 20px; background: rgba(255,255,255,0.95); padding: 10px 15px; border-radius: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); z-index: 1000; font-size: 14px; border: 1px solid #e0e0e0;">
                    <div id="usage-text"></div>
                </div>
            `;
      $('body').append(indicatorHTML);
      indicator = $('#usage-indicator');
    }

    // Update indicator text
    if (this.usageData.is_subscribed) {
      $('#usage-text').html('<span style="color: #27ae60;">✓ Pro Account</span>');
    } else {
      const remaining = this.usageData.remaining_trial_queries;
      const color = remaining <= 1 ? '#e74c3c' : remaining <= 2 ? '#f39c12' : '#3498db';
      $('#usage-text').html(`<span style="color: ${color};">Free: ${remaining}/5 left</span>`);
    }
  },

  // Open subscription dialog
  openSubscriptionDialog: function () {
    // Find existing subscription dialog or create one
    const dialog = document.querySelector('#subscribe-dialog');
    if (dialog && dialog.showModal) {
      dialog.showModal();
    } else if (window.dialogPolyfill) {
      window.dialogPolyfill.registerDialog(dialog);
      dialog.showModal();
    } else {
      // Fallback: navigate to subscription page
      window.location.href = '/subscribe';
    }
  },

  // Mark upsell as shown on server
  markUpsellShown: function () {
    if (window.userData && window.userData.secret) {
      fetch('/api/mark-upsell-shown', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret: window.userData.secret
        })
      }).catch(err => {
        console.warn('Failed to mark upsell as shown:', err);
      });
    }
  }
};

// Auto-initialize when DOM is ready
$(document).ready(function () {
  window.UsageTracker.init();
});
